import { ReactElement, useMemo } from 'react'
import { TransmitterMesh } from '@scene'
import { useCameraStore } from '@modugen/ui-toolkit'
import { useControlStore, useModelStore } from '@editorStores'
import { useHiddenElements, useIfcImportSelectionActive } from '@editorHooks'
import { isPointerModifierPressed } from 'src/shortcuts/pointerModifiers'

export interface LocalTensileTransmittersPreviewProps {
  graph: TensileTransmissionGraph
  selectedElement: string
  connectorGuid?: string | null
  onClick: (connectorGuid: string, elementGuid: string) => void
}

/**
 * Draws the tensile transmitters of the element being edited from the (unsaved) form graph.
 * The global tensile transmitter mesh hides this element meanwhile (useHideTensileTransmitters).
 * Like the global mesh it is 3D only and ignores clicks in hide mode, while the IFC import
 * selection is active and with the multi-select modifier pressed.
 */
const LocalTensileTransmittersPreview = ({
  graph,
  selectedElement,
  connectorGuid,
  onClick,
}: LocalTensileTransmittersPreviewProps): ReactElement | null => {
  const domains = useModelStore(state => state.domains)
  const showConnectors = useControlStore(
    state => state.structuralPlanningElementVisibility.connectors,
  )
  const isOrthographic = useCameraStore(state => state.isOrthographic)
  const actionMode = useControlStore(state => state.actionMode)
  const isIfcImportSelectionActive = useIfcImportSelectionActive()
  const hiddenElements = useHiddenElements()

  const elementGuids = useMemo(() => [selectedElement], [selectedElement])

  if (!showConnectors || isOrthographic) return null

  return (
    <TransmitterMesh
      data={graph}
      domains={domains}
      transmitterGuid={connectorGuid || undefined}
      onClick={({ guid, element_guid }) => {
        if (isPointerModifierPressed('multiSelect')) return

        onClick(guid, element_guid)
      }}
      elementGuids={elementGuids}
      hiddenElements={hiddenElements}
      disableClick={actionMode === 'hide' || isIfcImportSelectionActive}
    />
  )
}

export default LocalTensileTransmittersPreview
